import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import useWaitlistSignup from '../hooks/useWaitlistSignup';
import styles from './WaitlistSignup.module.css';

/**
 * Standalone "join the waitlist" block (parchment theme) for use outside the hero,
 * e.g. at the end of a journal post. Shares its behaviour with the hero form via
 * useWaitlistSignup.
 */
export default function WaitlistSignup({
  id = 'waitlist_block',
  formName = 'waitlist_block',
  inputId = 'waitlist-block-email',
  heading = 'Join the waitlist',
  blurb,
  buttonLabel = 'Join the waitlist!',
  variant = 'default',
}) {
  const waitlist = useWaitlistSignup({ id, formName });

  return (
    <section
      className={`${styles.block} ${variant === 'drawer' ? styles.drawerBlock : ''}`}
      aria-labelledby={`${inputId}-heading`}
    >
      <h2 id={`${inputId}-heading`} className={styles.heading}>
        {heading}
      </h2>
      {blurb ? <p className={styles.blurb}>{blurb}</p> : null}

      <form className={styles.form} onSubmit={waitlist.handleSubmit}>
        <input
          id={inputId}
          type="email"
          value={waitlist.email}
          onFocus={waitlist.handleEmailFocus}
          onChange={waitlist.handleEmailChange}
          placeholder="Enter your email"
          className={styles.input}
          required
          aria-label="Email for waitlist"
        />
        <button
          type="submit"
          className={styles.button}
          disabled={waitlist.status === 'submitting' || !waitlist.recaptchaEnabled}
        >
          {buttonLabel}
        </button>
      </form>

      {waitlist.showNewsletterOptIn && (
        <label className={styles.optIn} htmlFor={`${inputId}-newsletter`}>
          <input
            id={`${inputId}-newsletter`}
            type="checkbox"
            checked={waitlist.newsletterOptIn}
            onChange={(event) => waitlist.setNewsletterOptIn(event.target.checked)}
            className={styles.optInBox}
          />
          <span>Subscribe to our newsletter</span>
        </label>
      )}

      <div className={styles.meta}>
        {waitlist.recaptchaEnabled && waitlist.showRecaptcha ? (
          <div className={styles.recaptcha}>
            <ReCAPTCHA
              ref={waitlist.recaptchaRef}
              sitekey={waitlist.recaptchaSiteKey}
              onChange={waitlist.handleRecaptchaChange}
              onExpired={waitlist.handleRecaptchaExpired}
              onErrored={waitlist.handleRecaptchaErrored}
            />
          </div>
        ) : !waitlist.recaptchaEnabled ? (
          <p className={styles.error}>reCAPTCHA is unavailable right now.</p>
        ) : null}

        {waitlist.recaptchaError && <p className={styles.error}>{waitlist.recaptchaError}</p>}
      </div>

      {waitlist.popup.isOpen && (
        <div className={styles.popupOverlay} role="dialog" aria-modal="true" aria-live="polite">
          <div className={styles.popupCard}>
            {waitlist.popup.phase === 'loading' ? (
              <>
                <div className={styles.popupSpinner} />
                <h3 className={styles.popupTitle}>Please wait</h3>
                <p className={styles.popupMessage}>{waitlist.popup.message}</p>
              </>
            ) : (
              <>
                {waitlist.popup.phase === 'success' && (
                  <div className={styles.popupSuccessIcon}>
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                )}
                <h3 className={styles.popupTitle}>
                  {waitlist.popup.phase === 'success'
                    ? 'You\u2019re on the list'
                    : 'Something went wrong'}
                </h3>
                <p className={styles.popupMessage}>{waitlist.popup.message}</p>
                <button
                  ref={waitlist.popupButtonRef}
                  type="button"
                  className={styles.popupButton}
                  onClick={waitlist.closePopup}
                >
                  Got it
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
